/**
 * The file rules of every release-kit asset (spec §3): pixel size, length, container, audio, loop
 * and text. music24-api renders to these sizes and checks every file against them
 * (`checkAssetFile`). The file has no imports, so every project can use it.
 */

export type AssetSpecId =
  | 'story916'
  | 'feed45'
  | 'feed11'
  | 'bannerYoutube'
  | 'bannerX'
  | 'bannerFacebook'
  | 'spotifyHeader'
  | 'thumbnailYoutube'
  | 'cover3000'
  | 'cover1400'
  | 'cover640'
  | 'lyricCard11'
  | 'linkPreview'
  | 'short916'
  | 'short11'
  | 'short45'
  | 'canvas'
  | 'motionSquare'
  | 'motionTall'
  | 'full169';

export interface AssetSpec {
  id: AssetSpecId;
  kind: 'image' | 'video';
  width: number;
  height: number;
  /** JPEG for photos, PNG only where the destination prefers it. */
  container: 'jpg' | 'png' | 'mp4';
  minSec?: number;
  maxSec?: number;
  fps?: number;
  audio?: boolean;
  loop?: boolean;
  textAllowed: boolean;
  maxBytes: number;
  /** The lowest mean video rate a file may have, in kbit/s (the motion artwork: Apple's 45 Mbit/s floor). */
  minKbps?: number;
}

const MB = 1024 * 1024;

const image = (id: AssetSpecId, width: number, height: number, maxBytes = 10 * MB, textAllowed = true): AssetSpec => ({
  id,
  kind: 'image',
  width,
  height,
  container: 'jpg',
  textAllowed,
  maxBytes,
});

const shortVideo = (id: AssetSpecId, width: number, height: number): AssetSpec => ({
  id,
  kind: 'video',
  width,
  height,
  container: 'mp4',
  minSec: 15,
  maxSec: 30,
  fps: 30,
  audio: true,
  textAllowed: true,
  maxBytes: 100 * MB,
});

export const ASSET_SPECS: Readonly<Record<AssetSpecId, AssetSpec>> = {
  story916: image('story916', 1080, 1920),
  feed45: image('feed45', 1080, 1350),
  feed11: image('feed11', 1080, 1080),
  bannerYoutube: image('bannerYoutube', 2560, 1440),
  bannerX: image('bannerX', 1500, 500),
  bannerFacebook: image('bannerFacebook', 851, 315),
  /**
   * The Spotify artist page header. Checked against the Spotify for Artists upload screen on
   * 2026-10-17 (owner screenshot): JPEG, GIF or PNG, at least 2660 x 1140, "avoid text, logos, and
   * busy backgrounds". Spotify draws the photo and the name over it (`spotifyHeader.ts`).
   */
  spotifyHeader: image('spotifyHeader', 2660, 1140, 10 * MB, false),
  /** YouTube refuses a custom thumbnail above 2 MB. */
  thumbnailYoutube: image('thumbnailYoutube', 1280, 720, 2 * MB),
  /** Distributors refuse cover art with text that is not the title or the artist: the art carries none. */
  cover3000: image('cover3000', 3000, 3000, 10 * MB, false),
  cover1400: image('cover1400', 1400, 1400, 10 * MB, false),
  cover640: image('cover640', 640, 640, 10 * MB, false),
  lyricCard11: image('lyricCard11', 1080, 1080),
  linkPreview: image('linkPreview', 1200, 630, 1 * MB),
  short916: shortVideo('short916', 1080, 1920),
  short11: shortVideo('short11', 1080, 1080),
  short45: shortVideo('short45', 1080, 1350),
  /**
   * Spotify Canvas. Checked against the Spotify for Artists "Canvas guidelines" on 2026-10-04:
   * vertical 9:16, MP4 (or JPG), 3–8 s, no talking or singing, no rapid cuts or flashing, a loop,
   * no song or artist name (the Now Playing view shows it). The page gives no byte limit; the owner
   * chose 8 MB (2026-10-04), the limit that third-party guides give, so no upload fails for size.
   * 1080 × 1920 is the size that the page recommends for modern screens.
   */
  canvas: {
    id: 'canvas',
    kind: 'video',
    width: 1080,
    height: 1920,
    container: 'mp4',
    minSec: 3,
    maxSec: 8,
    fps: 30,
    audio: false,
    loop: true,
    textAllowed: false,
    maxBytes: 8 * MB,
  },
  /**
   * Apple Music motion artwork: a loop with no audio, 15 to 30 s (whole bars near 20 s; the Studio
   * lengths `MOTION_ARTWORK_LENGTHS` override), square 3840 × 3840 and tall 2048 × 2732. An H.264
   * MP4 at a constant 52 Mbit/s (design w, owner answer 2026-10-08: Apple asks for 45 to 100 Mbit/s;
   * `MOTION_ARTWORK_RATE`), about 130 MB for 20 s, 200 MB for 30 s. The MP4 plays in a browser, so
   * it is its own preview. The ProRes 422 HQ MOV is a separate download on request
   * (`kit/proRes/`), never a kit file.
   */
  motionSquare: {
    id: 'motionSquare',
    kind: 'video',
    width: 3840,
    height: 3840,
    container: 'mp4',
    minSec: 15,
    maxSec: 30,
    fps: 30,
    audio: false,
    loop: true,
    textAllowed: false,
    maxBytes: 240 * MB,
    minKbps: 45000,
  },
  motionTall: {
    id: 'motionTall',
    kind: 'video',
    width: 2048,
    height: 2732,
    container: 'mp4',
    minSec: 15,
    maxSec: 30,
    fps: 30,
    audio: false,
    loop: true,
    textAllowed: false,
    maxBytes: 240 * MB,
    minKbps: 45000,
  },
  /**
   * A full-song video (visualizer, lyric video, music video): the whole song or its trim, at least
   * 30 s and at most 900 s (the audio limit of music24-media). H.264 with the master as AAC. The
   * render caps the peak rate by the length (`fullVideoRateLimit`, music24-api), so a video of up
   * to about 7.5 minutes stays under the 300 MB Reel limit of Instagram; 900 s is about 600 MB.
   */
  full169: {
    id: 'full169',
    kind: 'video',
    width: 1920,
    height: 1080,
    container: 'mp4',
    minSec: 30,
    maxSec: 900,
    fps: 30,
    audio: true,
    textAllowed: true,
    maxBytes: 2048 * MB,
  },
};

export const ASSET_SPEC_IDS = Object.keys(ASSET_SPECS) as AssetSpecId[];

export function isAssetSpecId(value: unknown): value is AssetSpecId {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(ASSET_SPECS, value);
}
